import type { ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import { EnterTracking } from '@/components/EnterTracking';
import { useViewport } from '@/hooks';
import { FeaturedPeriods } from '@/module/featured/featuedQuiz.const';
import { useCreateSubjectNameGetter } from '@/shared/category';
import { useCachedCategoryInfo, useFetchCategoryByWithAuthUser } from '@/shared/category/useFetchCategoryInfo';
import { AllSchoolType } from '@/shared/category/util';
import { InfiniteListView } from '@/shared/components/itemList/InfiniteListView';
import { PLAIN_FRAME_BODY_CLASS_NAME, ProductContentFrame } from '@/shared/components/ProductContentFrame';
import { filterExternalPlatformQuizzes, useExternalPlatform } from '@/shared/externalPlatform';
import { useIsInProductContentFrame } from '@/shared/hooks/useIsInProductContentFrame';
import { PathParentLinkButton } from '@/shared/modules/navigation-path';
import { QuizListItem } from '@/shared/quizList';
import { DropdownSchool } from '@/shared/quizList/DropdownSchool/DropdownSchool';
import { EmptyQuizList } from '@/shared/quizList/EmptyQuizList';
import { useGetQuizListItemButtons } from '@/shared/quizList/hooks';
import { ListLayoutContainer, ListLayoutWrapper } from '@/shared/quizList/Layout';
import { cn } from '@/utils/tailwind.util';
import { useFetchQuizsetRankings } from './hook/useFetchQuizsetRankings';
import { type QuizsetRankingsSchoolType, useSelectedFeaturedQuiz } from './useSelectedFeaturedQuiz';

/** 인기 퀴즈 전체 보기. 좌측 메뉴·목록 버튼은 걸어온 길을 따르고, 길이 없으면 「퀴즈 자료실」로 간다. */
export const FeaturedQuizPageView = () => {
  useFetchCategoryByWithAuthUser();
  // 카드 스크롤 복원(ready)이 목록과 같은 조회를 보도록 여기서 한 번만 조회해 내려준다.
  const {
    data,
    isLoading,
    isError
  } = useFetchQuizsetRankings();
  const {
    mode
  } = useExternalPlatform();
  const quizzes = filterExternalPlatformQuizzes(data?.data ?? [], mode);
  return <ProductContentFrame ready={!isLoading} hasBanner={false} parentLink={<PathParentLinkButton placement="plain" />} data-sentry-element="ProductContentFrame" data-sentry-component="FeaturedQuizPageView" data-sentry-source-file="FeaturedQuizPageView.tsx">
      <Body data-sentry-element="Body" data-sentry-source-file="FeaturedQuizPageView.tsx">
        <Header data-sentry-element="Header" data-sentry-source-file="FeaturedQuizPageView.tsx" />
        <div className={'w-full'}>
          <Content quizzes={quizzes} isLoading={isLoading} isError={isError} data-sentry-element="Content" data-sentry-source-file="FeaturedQuizPageView.tsx" />
        </div>
      </Body>
    </ProductContentFrame>;
};

/** 카드 안에서는 배너 없는 카드 본문 여백을 쓰고 최대 폭 없이 카드를 채운다. */
const Body = ({
  children
}: {
  children: ReactNode;
}) => {
  const inFrame = useIsInProductContentFrame();
  return <ListLayoutWrapper className={cn(inFrame && PLAIN_FRAME_BODY_CLASS_NAME)} data-sentry-element="ListLayoutWrapper" data-sentry-component="Body" data-sentry-source-file="FeaturedQuizPageView.tsx">
      <ListLayoutContainer className={cn(inFrame && 'max-w-none')} data-sentry-element="ListLayoutContainer" data-sentry-source-file="FeaturedQuizPageView.tsx">
        {children}
      </ListLayoutContainer>
    </ListLayoutWrapper>;
};
const Content = ({
  quizzes,
  isLoading,
  isError
}: {
  quizzes: SearchQuizsetRes[];
  isLoading: boolean;
  isError: boolean;
}) => {
  const {
    categoryTreeData
  } = useCachedCategoryInfo();
  const subjectNameGetter = useCreateSubjectNameGetter(categoryTreeData?.subjectTree);
  const getQuizButtons = useGetQuizListItemButtons('featured');
  const {
    selectedPeriod,
    selectedSchoolType
  } = useSelectedFeaturedQuiz();
  return <InfiniteListView items={quizzes} fetchNextPage={() => {}} isLoading={isLoading} isError={isError} hasNextPage={false} EmptyComponent={<EmptyQuizList type={'featured'} />} renderItem={quiz => <QuizListItem key={quiz.id} quizSet={quiz} menuItems={getQuizButtons(quiz)} subjectName={subjectNameGetter({
    subjectGroupId: quiz.categoryInfo?.subject,
    subSubjectGroupId: quiz.categoryInfo?.subSubject
  })} eventLocation={'featured'} isHideAuthor={false} alwaysDropDownMenu={false} eventAdditionalProperty={{
    featured: {
      location: 'featured',
      period: selectedPeriod,
      schoolType: selectedSchoolType
    }
  }} />} data-sentry-element="InfiniteListView" data-sentry-component="Content" data-sentry-source-file="FeaturedQuizPageView.tsx" />;
};
const Header = () => {
  return <div className={'flex w-full flex-col gap-md max-mobile-lg:gap-xs'} data-sentry-component="Header" data-sentry-source-file="FeaturedQuizPageView.tsx">
      <HeaderTop data-sentry-element="HeaderTop" data-sentry-source-file="FeaturedQuizPageView.tsx" />
      <HeaderBottom data-sentry-element="HeaderBottom" data-sentry-source-file="FeaturedQuizPageView.tsx" />
    </div>;
};
const HeaderTop = () => {
  const {
    selectedSchoolType,
    setSelectedSchoolType
  } = useSelectedFeaturedQuiz();
  const {
    categoryTreeData
  } = useCachedCategoryInfo();
  const schoolTypes = [{
    name: AllSchoolType.name,
    value: AllSchoolType.value
  }, ...(categoryTreeData?.schoolInfoTree.children ?? []).map(d => ({
    name: d.name,
    value: d.name
  }))];
  const {
    isMobile
  } = useViewport();
  const {
    t
  } = useTranslation(['common']);
  return <div className={'flex items-center gap-xs pb-sm pt-xs'} data-sentry-component="HeaderTop" data-sentry-source-file="FeaturedQuizPageView.tsx">
      <DropdownSchool selectedSchoolTypeName={selectedSchoolType} onSelectedSchoolType={v => setSelectedSchoolType(v as QuizsetRankingsSchoolType)} schoolTypes={schoolTypes} data-sentry-element="DropdownSchool" data-sentry-source-file="FeaturedQuizPageView.tsx" />
      <span className={'text-heading-xl-extrabold'}>
        {isMobile ? t('category.popular') : t('common:featured.recommendFeaturedQuiz')}
      </span>
      <span className={'text-heading-xl-extrabold '}></span>
    </div>;
};
const HeaderBottom = () => {
  const {
    setSelectedPeriod,
    selectedPeriod,
    selectedSchoolType
  } = useSelectedFeaturedQuiz();
  const {
    t
  } = useTranslation(['common']);
  return <EnterTracking eventName={'enter_featured_page'} eventProperties={{
    schoolType: selectedSchoolType,
    period: selectedPeriod
  }} data-sentry-element="EnterTracking" data-sentry-component="HeaderBottom" data-sentry-source-file="FeaturedQuizPageView.tsx">
      <div className={'flex w-full justify-between'}>
        <div className={'flex h-2xl w-fit gap-md'}>
          {FeaturedPeriods.map(period => <div className={cn('flex h-full box-border items-center px-4xs cursor-pointer py-2xs text-heading-md-bold', period.value === selectedPeriod ? 'border-b-[1px] border-[#5D5E69] text-default' : 'border-b-[1px] border-transparent text-assistive')} key={period.value} onClick={() => {
          void setSelectedPeriod(period.value);
          period.track({
            schoolType: selectedSchoolType,
            location: 'featured'
          });
        }}>
              {t(period.name)}
            </div>)}
        </div>
        <div className={'flex  h-2xl w-fit items-center p-sm text-body-md-semibold text-assistive'}>
          {t('common:featured.shownMaxQuiz')}
        </div>
      </div>
    </EnterTracking>;
};